import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Table, Badge, Spinner, Modal, Form } from 'react-bootstrap';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
  useRunEntrepreneurBillingMutation,
  useGetEntrepreneurFirmBillingSummaryQuery,
  useGetEntrepreneurFirmBillingStatusQuery,
  useGetEntrepreneurFirmSetupIntentQuery,
  useSetEntrepreneurFirmPaymentMethodMutation,
  useRemoveEntrepreneurFirmPaymentMethodMutation,
  useRefundEntrepreneurInvoiceMutation,
} from '../../app/api';
import { brand, usd, CombinedChargeNote } from './common';

/* Firm billing UI, shared by the firm drill-down (FirmDetail). Split out of the
 * old FirmDetails page so the payment-method, invoices, and refund pieces live
 * in one place and the drill-down page stays readable. */

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : '—');

const STATUS_VARIANT = { PAID: 'success', FAILED: 'danger', VOID: 'secondary', DRAFT: 'secondary', REFUNDED: 'warning' };

function StatusBadge({ status }) {
  const variant = STATUS_VARIANT[status] || 'secondary';
  return <Badge bg={variant} text={variant === 'warning' ? 'dark' : undefined}>{status}</Badge>;
}
StatusBadge.propTypes = { status: PropTypes.string };

const formatCardBrand = (b) => (b ? b.charAt(0).toUpperCase() + b.slice(1) : 'Card');

// Sum of REFUNDED rows already targeting a given charge row, from the same
// list — lets the UI hide/relabel "Refund" once a charge is fully refunded
// without a second round-trip (the backend re-validates regardless).
function refundedCentsFor(invoiceId, rows) {
  return rows
    .filter((r) => r.refund_of_id === invoiceId && r.status === 'REFUNDED')
    .reduce((sum, r) => sum + Math.abs(r.total_charged_cents), 0);
}

// ── Refund modal — full remaining amount or a specific dollar amount ──────────
function RefundModal({ invoice, kind, remainingCents, onClose, onRefunded }) {
  const [refundInvoice, { isLoading: submitting }] = useRefundEntrepreneurInvoiceMutation();
  const [mode, setMode] = useState('full');
  const [customAmount, setCustomAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);

  const label = kind === 'usage'
    ? `${invoice.period_year}-${String(invoice.period_month).padStart(2, '0')} usage charge`
    : `seat activation charge for ${invoice.member_email || 'this member'}`;

  const submit = async () => {
    setError(null);
    let amountCents = null;
    if (mode === 'custom') {
      const dollars = parseFloat(customAmount);
      if (!Number.isFinite(dollars) || dollars <= 0) { setError('Enter a valid amount.'); return; }
      amountCents = Math.round(dollars * 100);
      if (amountCents > remainingCents) { setError(`Only ${usd(remainingCents)} remains refundable on this charge.`); return; }
    }
    try {
      await refundInvoice({ kind, invoice_id: invoice.id, amount_cents: amountCents, reason: reason.trim() || undefined }).unwrap();
      await onRefunded();
    } catch (err) {
      setError(err?.data?.detail || 'Refund failed.');
    }
  };

  return (
    <Modal show onHide={submitting ? undefined : onClose} centered>
      <Modal.Header closeButton={!submitting}>
        <Modal.Title style={{ fontSize: '1.1rem' }}>Refund charge</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p style={{ fontSize: '0.85rem', color: brand.charcoal }}>
          Refunding the {label} — <strong>{usd(remainingCents)}</strong> remains refundable.
        </p>
        <Form.Check
          type="radio" id="refund-mode-full" name="refund-mode" className="mb-2"
          label={`Full remaining amount (${usd(remainingCents)})`}
          checked={mode === 'full'} onChange={() => setMode('full')} disabled={submitting}
        />
        <Form.Check
          type="radio" id="refund-mode-custom" name="refund-mode" className="mb-2"
          label="Specific amount" checked={mode === 'custom'} onChange={() => setMode('custom')} disabled={submitting}
        />
        {mode === 'custom' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', marginLeft: '1.5rem' }}>
            <span>$</span>
            <Form.Control
              type="number" min="0.01" step="0.01" value={customAmount} autoFocus disabled={submitting}
              onChange={(e) => setCustomAmount(e.target.value)} style={{ width: 140 }}
            />
          </div>
        )}
        <Form.Group className="mb-2">
          <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600 }}>Reason (optional)</Form.Label>
          <Form.Control
            type="text" value={reason} onChange={(e) => setReason(e.target.value)} disabled={submitting}
            placeholder="e.g. billing error, customer request"
          />
        </Form.Group>
        {error && <div style={{ color: brand.red, fontSize: '0.82rem' }}>{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="danger" onClick={submit} disabled={submitting}>
          {submitting ? <><Spinner size="sm" animation="border" className="me-1" />Refunding…</> : 'Issue refund'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
RefundModal.propTypes = {
  invoice: PropTypes.object.isRequired,
  kind: PropTypes.oneOf(['usage', 'activation']).isRequired,
  remainingCents: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onRefunded: PropTypes.func.isRequired,
};

// ── Payment method — card collection via Stripe Elements ──────────────────────
// Mirrors b2b/Dashboard.jsx's self-service CardForm/CardFields (same Stripe
// SetupIntent + Payment Element flow) but hitting the employee-side endpoints
// and scoped by firm_id instead of the caller's own session.
function CardFields({ firmId, onSaved, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [setPaymentMethod] = useSetEntrepreneurFirmPaymentMethodMutation();

  const submit = async () => {
    setErr(null);
    if (!name.trim()) { setErr('Enter the name on the card.'); return; }
    if (!stripe || !elements) { setErr('Payment form isn’t ready yet.'); return; }
    setSaving(true);
    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
        confirmParams: { payment_method_data: { billing_details: { name: name.trim() } } },
      });
      if (error) { setErr(error.message || 'Could not save the card.'); setSaving(false); return; }
      await setPaymentMethod({ firm_id: firmId, setup_intent_id: setupIntent.id }).unwrap();
      await onSaved();
    } catch (e) {
      setErr(e?.data?.detail || 'Could not save the card.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Form.Group className="mb-2">
        <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600 }}>Name on card</Form.Label>
        <Form.Control value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jane Smith" autoComplete="cc-name" disabled={saving} />
      </Form.Group>
      <PaymentElement options={{ wallets: { link: 'never' }, fields: { billingDetails: { name: 'never', email: 'never' } } }} />
      {err && <div style={{ color: brand.red, fontSize: '0.82rem', marginTop: '0.6rem' }}>{err}</div>}
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.85rem' }}>
        <Button size="sm" variant="success" onClick={submit} disabled={saving || !stripe}>
          {saving ? 'Saving…' : 'Save card'}
        </Button>
        <Button size="sm" variant="outline-secondary" onClick={onCancel} disabled={saving}>Cancel</Button>
      </div>
    </div>
  );
}
CardFields.propTypes = { firmId: PropTypes.number.isRequired, onSaved: PropTypes.func.isRequired, onCancel: PropTypes.func.isRequired };

function CardChangeForm({ firmId, publishableKey, onSaved, onCancel }) {
  const { data, isLoading, error } = useGetEntrepreneurFirmSetupIntentQuery({ firm_id: firmId });
  const stripePromise = useMemo(() => (publishableKey ? loadStripe(publishableKey) : null), [publishableKey]);

  if (error) return <div style={{ color: brand.red, fontSize: '0.82rem' }}>Could not start the payment form.</div>;
  if (isLoading || !stripePromise || !data?.client_secret) {
    return <div className="text-center p-3"><Spinner size="sm" /></div>;
  }
  return (
    <Elements stripe={stripePromise} options={{ clientSecret: data.client_secret, appearance: { theme: 'stripe' } }}>
      <CardFields firmId={firmId} onSaved={onSaved} onCancel={onCancel} />
    </Elements>
  );
}
CardChangeForm.propTypes = {
  firmId: PropTypes.number.isRequired,
  publishableKey: PropTypes.string,
  onSaved: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export function PaymentMethodSection({ firmId, onChanged }) {
  const { data: status, isLoading, refetch } = useGetEntrepreneurFirmBillingStatusQuery({ firm_id: firmId });
  const [removePaymentMethod, { isLoading: removing }] = useRemoveEntrepreneurFirmPaymentMethodMutation();
  const [changing, setChanging] = useState(false);
  const [notice, setNotice] = useState(null);

  const afterChange = async () => {
    await refetch();
    if (onChanged) await onChanged();
  };

  const onSaved = async () => {
    setNotice('Payment method saved.');
    setChanging(false);
    await afterChange();
  };

  const onRemove = async () => {
    if (!window.confirm('Remove this firm’s card on file? Automatic billing stops until a new one is added.')) return;
    try {
      await removePaymentMethod({ firm_id: firmId }).unwrap();
      setNotice('Payment method removed.');
      await afterChange();
    } catch (err) {
      window.alert(err?.data?.detail || 'Could not remove the payment method.');
    }
  };

  if (isLoading) return <div className="text-center p-3"><Spinner size="sm" /></div>;
  if (!status?.configured) {
    return <div style={{ color: brand.charcoal, fontSize: '0.85rem' }}>Payments aren&apos;t configured.</div>;
  }

  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <h6 style={{ color: brand.deepBlue, fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Payment method</h6>
      {notice && <div style={{ color: brand.green, fontSize: '0.8rem', marginBottom: '0.5rem' }}>{notice}</div>}
      {changing ? (
        <CardChangeForm
          firmId={firmId}
          publishableKey={status.publishable_key}
          onSaved={onSaved}
          onCancel={() => setChanging(false)}
        />
      ) : status.has_payment_method ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: brand.green, fontWeight: 600, fontSize: '0.9rem' }}>
            <i className="bi bi-check-circle-fill" />
            {formatCardBrand(status.card?.brand)} <span style={{ color: brand.charcoal, fontWeight: 700, letterSpacing: 1 }}>•••• {status.card?.last4 || '????'}</span>
            {status.card?.exp_month && status.card?.exp_year && (
              <span style={{ color: brand.charcoal, fontWeight: 500 }}>
                exp {String(status.card.exp_month).padStart(2, '0')}/{String(status.card.exp_year).slice(-2)}
              </span>
            )}
          </span>
          <Button size="sm" variant="outline-primary" onClick={() => setChanging(true)}>Change card</Button>
          <Button size="sm" variant="outline-danger" onClick={onRemove} disabled={removing}>
            {removing ? 'Removing…' : 'Remove'}
          </Button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <span style={{ color: brand.goldenrod, fontWeight: 600, fontSize: '0.9rem' }}>
            <i className="bi bi-exclamation-triangle-fill me-1" />No card on file
          </span>
          <Button size="sm" variant="success" onClick={() => setChanging(true)}>Add card</Button>
        </div>
      )}
    </div>
  );
}
PaymentMethodSection.propTypes = { firmId: PropTypes.number.isRequired, onChanged: PropTypes.func };

// ── Firm billing panel — summary, payment method, invoices + refunds ──────────
export function FirmBillingPanel({ firmId, firmName, onChanged }) {
  const { data, isLoading, error, refetch } = useGetEntrepreneurFirmBillingSummaryQuery({ firm_id: firmId });
  const [runBilling] = useRunEntrepreneurBillingMutation();
  const [refundTarget, setRefundTarget] = useState(null); // { invoice, kind, remainingCents }

  const runBillingNow = async (month) => {
    const label = month ? `for ${month}` : 'for the last closed month';
    if (!window.confirm(`Check ${firmName}'s Claude billing balance ${label} and charge anything still owed?`)) return;
    try {
      const res = await runBilling({ firm_id: firmId, month: month || undefined }).unwrap();
      const msg = res.result === 'skipped' && res.reason === 'fully_billed'
        ? `${res.period}: already fully billed — nothing owed.`
        : `${res.period}: ${res.result}${res.error ? ` — ${res.error}` : ''}.`;
      window.alert(msg);
      await refetch();
    } catch (err) {
      window.alert(err?.data?.detail || 'Billing run failed.');
    }
  };

  const checkSpecificMonth = () => {
    const input = window.prompt(`Which month should we check for ${firmName}? Format: YYYY-MM`, '');
    if (!input) return;
    if (!/^\d{4}-\d{2}$/.test(input.trim())) { window.alert('Enter a month as YYYY-MM, e.g. 2026-06.'); return; }
    runBillingNow(input.trim());
  };

  if (isLoading) return <div className="text-center p-4"><Spinner size="sm" /></div>;
  if (error || !data) return <div style={{ color: brand.red, fontSize: '0.85rem', padding: '1rem 0' }}>Could not load billing details.</div>;

  const allRows = [...data.usage_invoices, ...data.activation_invoices];

  return (
    <div>
      <PaymentMethodSection firmId={firmId} onChanged={onChanged} />

      <div style={{ background: brand.softGray, borderRadius: 8, padding: '0.85rem 1rem', marginBottom: '1.25rem', fontSize: '0.85rem' }}>
        <div>
          Next charge will run on <strong>{data.next_billing_date}</strong> for usage currently at{' '}
          <strong>{usd(data.current_period.unbilled_cents)}</strong>
          {' '}({MONTH_NAMES[data.current_period.month - 1]} {data.current_period.year} so far).
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem' }}>
          <Button size="sm" variant="outline-secondary" onClick={() => runBillingNow()}>Run billing now</Button>
          <Button size="sm" variant="outline-secondary" onClick={checkSpecificMonth}>Check a month…</Button>
        </div>
      </div>

      <h6 style={{ color: brand.deepBlue, fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Monthly usage invoices</h6>
      {data.usage_invoices.length === 0 ? (
        <div style={{ color: brand.charcoal, fontSize: '0.82rem', marginBottom: '1.25rem' }}>None yet.</div>
      ) : (
        <Table size="sm" responsive className="mb-4 bg-white" style={{ fontSize: '0.8rem' }}>
          <thead><tr style={{ color: brand.charcoal }}>
            <th>Period</th><th className="text-end">Usage</th><th className="text-end">Fee</th><th className="text-end">Total</th><th>Status</th><th />
          </tr></thead>
          <tbody>
            {data.usage_invoices.map((inv) => {
              const isRefund = Boolean(inv.refund_of_id);
              const refunded = isRefund ? 0 : refundedCentsFor(inv.id, allRows);
              const remaining = inv.total_charged_cents - refunded;
              const canRefund = !isRefund && inv.status === 'PAID' && inv.stripe_payment_intent_id && remaining > 0;
              return (
                <tr key={inv.id}>
                  <td>
                    {inv.period_year}-{String(inv.period_month).padStart(2, '0')}
                    <CombinedChargeNote combined={inv.combined_charge} status={inv.status} />
                  </td>
                  <td className="text-end" style={{ color: isRefund ? brand.red : undefined }}>{usd(inv.usage_cost_cents)}</td>
                  <td className="text-end">{usd(inv.fee_cents)}</td>
                  <td className="text-end" style={{ fontWeight: 700, color: isRefund ? brand.red : brand.deepBlue }}>{usd(inv.total_charged_cents)}</td>
                  <td><StatusBadge status={inv.status} />{refunded > 0 && !isRefund && <div style={{ fontSize: '0.68rem', color: brand.charcoal }}>{usd(refunded)} refunded</div>}</td>
                  <td className="text-end">
                    {canRefund && (
                      <Button size="sm" variant="outline-danger" onClick={() => setRefundTarget({ invoice: inv, kind: 'usage', remainingCents: remaining })}>
                        Refund
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <h6 style={{ color: brand.deepBlue, fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Seat activation charges</h6>
      {data.activation_invoices.length === 0 ? (
        <div style={{ color: brand.charcoal, fontSize: '0.82rem', marginBottom: '1.25rem' }}>None yet.</div>
      ) : (
        <Table size="sm" responsive className="mb-4 bg-white" style={{ fontSize: '0.8rem' }}>
          <thead><tr style={{ color: brand.charcoal }}>
            <th>Member</th><th>Date</th><th className="text-end">Base</th><th className="text-end">Tax</th><th className="text-end">Fee</th><th className="text-end">Total</th><th>Status</th><th />
          </tr></thead>
          <tbody>
            {data.activation_invoices.map((inv) => {
              const isRefund = Boolean(inv.refund_of_id);
              const refunded = isRefund ? 0 : refundedCentsFor(inv.id, allRows);
              const remaining = inv.total_charged_cents - refunded;
              const canRefund = !isRefund && inv.status === 'PAID' && inv.stripe_payment_intent_id && remaining > 0;
              return (
                <tr key={inv.id}>
                  <td>
                    {inv.member_email || '—'}
                    {inv.note && <div style={{ fontSize: '0.72rem', color: brand.charcoal, overflowWrap: 'anywhere' }}>{inv.note}</div>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(inv.created)}</td>
                  <td className="text-end" style={{ color: isRefund ? brand.red : undefined }}>{usd(inv.base_cents)}</td>
                  <td className="text-end">{usd(inv.tax_cents)}</td>
                  <td className="text-end">{usd(inv.fee_cents)}</td>
                  <td className="text-end" style={{ fontWeight: 700, color: isRefund ? brand.red : brand.deepBlue }}>{usd(inv.total_charged_cents)}</td>
                  <td><StatusBadge status={inv.status} />{refunded > 0 && !isRefund && <div style={{ fontSize: '0.68rem', color: brand.charcoal }}>{usd(refunded)} refunded</div>}</td>
                  <td className="text-end">
                    {canRefund && (
                      <Button size="sm" variant="outline-danger" onClick={() => setRefundTarget({ invoice: inv, kind: 'activation', remainingCents: remaining })}>
                        Refund
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <div style={{ background: brand.softGray, borderRadius: 8, padding: '0.85rem 1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: brand.deepBlue, fontWeight: 700 }}>Total charged (all-time)</span>
          <span style={{ color: brand.deepBlue, fontWeight: 800, fontSize: '1.15rem' }}>{usd(data.total_charged_cents)}</span>
        </div>
        {Object.keys(data.total_by_year).length > 0 && (
          <Table size="sm" className="mt-2 mb-0">
            <tbody>
              {Object.entries(data.total_by_year).reverse().map(([year, cents]) => (
                <tr key={year}>
                  <td style={{ color: brand.charcoal, fontSize: '0.8rem' }}>{year}</td>
                  <td className="text-end" style={{ fontWeight: 600, color: brand.deepBlue, fontSize: '0.8rem' }}>{usd(cents)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      {refundTarget && (
        <RefundModal
          invoice={refundTarget.invoice}
          kind={refundTarget.kind}
          remainingCents={refundTarget.remainingCents}
          onClose={() => setRefundTarget(null)}
          onRefunded={async () => { setRefundTarget(null); await refetch(); }}
        />
      )}
    </div>
  );
}
FirmBillingPanel.propTypes = {
  firmId: PropTypes.number.isRequired,
  firmName: PropTypes.string,
  onChanged: PropTypes.func,
};
